import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { css } from '@emotion/react';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { config } from '@config/pendo.config.json';
import { useMentoringProgramActionItems } from 'athena/hooks/use-mentoring-program-action-items';
import useConnection from 'athena/hooks/use-connection';
import { getMentoringProgramActionItems, getMentoringProgramActionItemsTotalCount } from 'redux/selectors/mentorship-program-action-items';
import { largeSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { fadeInAnimation } from './styles';
import { gray6 } from 'styles/global_defaults/colors';
import { ReactComponent as ActionItemsSectionIcon } from 'styles/icons/careers-trust.svg';
import ActionItem from 'athena/components/action-item';
import { ActionItemStatus } from 'redux/schemas/models/mentoring-program-action-items';

const INITIAL_VISIBLE_COUNT = 6;
const SCROLL_THRESHOLD = 12;

const styles = css`
  ${fadeInAnimation};
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: ${standardSpacing}px;
  padding: ${largeSpacing}px;
  gap: 12px;

  .action-items-header {
    height: 52px;
    gap: 4px;
  }

  .action-items-list {
    border-top: 1px solid ${gray6};
    padding-top: ${threeQuartersSpacing}px;
    gap: ${quarterSpacing}px;
  }
`;

const ReflectionActionItems = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: currentEnrollmentId,
  } = mentoringProgram || {};
  const { connectionId } = useConnection(currentEnrollmentId);

  const { ref: actionItemsEndRef, inView: isActionItemsEndReached } = useInView();
  const { loadMore } = useMentoringProgramActionItems({ mentorshipProgramId, connectionId, status: ActionItemStatus.COMPLETED });

  useEffect(() => {
    if (isExpanded && isActionItemsEndReached) {
      loadMore();
    }
  }, [isExpanded, isActionItemsEndReached, loadMore]);

  const totalCount = useSelector(getMentoringProgramActionItemsTotalCount(connectionId));
  const allActionItems = useSelector(getMentoringProgramActionItems(connectionId));
  const actionItems = allActionItems.filter((item) => item.status === ActionItemStatus.COMPLETED);

  useLayoutEffect(() => {
    if (!isExpanded || !listRef.current) {
      setListMaxHeight(null);
      return;
    }

    const children = Array.from(listRef.current.children);
    const itemCount = children.length - 1; // exclude sentinel div

    if (itemCount <= SCROLL_THRESHOLD) {
      setListMaxHeight(null);
      return;
    }

    const listEl = listRef.current;
    const listRect = listEl.getBoundingClientRect();
    const nthItem = children[SCROLL_THRESHOLD - 1] as HTMLElement;
    const nthItemRect = nthItem.getBoundingClientRect();
    setListMaxHeight(nthItemRect.bottom - listRect.top + listEl.scrollTop);
  }, [isExpanded, actionItems.length]);

  if (!actionItems.length) return null;

  const total = totalCount ?? actionItems.length;
  const hasMore = total > INITIAL_VISIBLE_COUNT;
  const visibleItems = isExpanded ? actionItems : actionItems.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = total - INITIAL_VISIBLE_COUNT;

  const handleToggle = () => setIsExpanded((prev) => !prev);

  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE.ACTION_ITEMS;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.programReflections.modal.actionItems;

  return (
    <div css={styles} className='d-flex flex-column text-left'>
      <div className='action-items-header d-flex align-items-center'>
        <p className='flex-grow-1 text-large-body mb-0'>
          {translationBase.HEADER(total)}
        </p>
        <ActionItemsSectionIcon width={40} height={40} />
      </div>
      <div
        ref={listRef}
        className='action-items-list d-flex flex-column'
        style={listMaxHeight != null ? { overflowY: 'auto', maxHeight: listMaxHeight } : undefined}
      >
        {visibleItems.map((item) => (
          <ActionItem key={item.id} item={item} isReadOnly />
        ))}
        <div ref={actionItemsEndRef} />
      </div>
      {hasMore && (
        <ClickableContainer
          className='toggle-show-more text-regular semi-bold gray-2 align-self-center'
          onClick={handleToggle}
          data-qa={isExpanded ? dataQATag.showLess : dataQATag.showMore}
          aria-expanded={isExpanded}
        >
          {isExpanded ? translationBase.SHOW_LESS() : translationBase.SHOW_MORE(hiddenCount)}
        </ClickableContainer>
      )}
    </div>
  );
};

export default ReflectionActionItems;
